import { TAGLink } from '@aspendental/shared-components-web';
import { FacilityFieldsFragment } from '@aspendental/shared-components-web/lib/graphql/__generated/sdk';
import { Form, SchemaBuilder, TextInput, useHookForm } from '@aspendental/shared-forms-web';
import CloseIcon from '@mui/icons-material/Close';
import PinDropIcon from '@mui/icons-material/PinDrop';
import { Box, Button, ButtonBase, Drawer, Stack, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';

import { findOfficeForm, findOfficeFormSchema, RenderDistance } from './FindOffice.helpers';

import {
	BffBrandType,
	BffFacility,
	useBffNearbyFacilitiesByLocationQuery,
	useBffNearbyFacilitiesByZipcodeQuery,
} from '@src/lib/__generated/beffe/sdk';

type FindOfficeDrawerProps = {
	open: boolean;
	onClose: () => void;
	handleChooseLocation: (data: any) => void;
	initialNearbyLocations: BffFacility[] | FacilityFieldsFragment[];
};

const schema = SchemaBuilder([...findOfficeFormSchema]);

const FindOfficeDrawer = ({ open, onClose, handleChooseLocation, initialNearbyLocations }: FindOfficeDrawerProps) => {
	const { t } = useTranslation('index');
	const [foundLocations, setFoundLocations] = useState<BffFacility[] | FacilityFieldsFragment[]>([]);
	const [zipCode, setZipCode] = useState('');
	const [latitude, setLatitude] = useState(0);
	const [longitude, setLongitude] = useState(0);
	const [radiusMiles, setRadiusMiles] = useState(250);

	const form = useHookForm({
		schema,
		defaultValues: { zipCode: '' },
	});

	useEffect(() => {
		setFoundLocations(initialNearbyLocations);
	}, [initialNearbyLocations]);

	const [{ data: facilitiesByZipData }] = useBffNearbyFacilitiesByZipcodeQuery({
		variables: { zipCode, brand: BffBrandType.ClearChoice, radiusMiles },
		pause: !zipCode,
	});

	const [{ data: facilitiesByLocationData }] = useBffNearbyFacilitiesByLocationQuery({
		variables: { latitude, longitude, brand: BffBrandType.ClearChoice },
		pause: latitude === 0 && longitude === 0,
	});

	useEffect(() => {
		if (!facilitiesByZipData?.facilitiesByZipcode?.length) {
			setRadiusMiles(500);
		} else {
			setFoundLocations(facilitiesByZipData?.facilitiesByZipcode as BffFacility[]);
		}
	}, [facilitiesByZipData]);

	useEffect(() => {
		if (facilitiesByLocationData?.facilityByLocation?.length) {
			setFoundLocations(facilitiesByLocationData?.facilityByLocation as BffFacility[]);
		}
	}, [facilitiesByLocationData]);

	const handleSubmit = async (fields: FieldValues) => {
		setZipCode(fields.zipCode);
		setRadiusMiles(250);
	};

	const handleUseCurrentLocation = () => {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords;
					setLatitude(latitude);
					setLongitude(longitude);
				},
				(error) => {
					console.error('Error getting location: ', error);
				}
			);
		} else {
			alert('Geolocation is not supported by this browser.');
		}
	};

	return (
		<Drawer
			anchor="right"
			open={Boolean(open)}
			onClose={onClose}
			PaperProps={{
				sx: {
					borderTopLeftRadius: '12px',
					borderBottomLeftRadius: '12px',
					width: { sm: '658px', xs: '95%' },
				},
			}}
		>
			<Stack sx={{ padding: { sm: '40px', xs: '30px' }, position: 'relative' }}>
				<Stack direction="row" justifyContent="space-between" mb={1.5}>
					<Typography variant="header3">{t('findAnOffice')}</Typography>
					<ButtonBase onClick={onClose}>
						<CloseIcon />
					</ButtonBase>
				</Stack>
				<Typography variant="bodyMediumBook">{t('enterYourZipForOffice')}</Typography>
				<Form form={form} onSubmit={handleSubmit}>
					<Stack direction="row" spacing={3} mt={1} maxWidth="350px" alignItems="center" mb={3}>
						<TextInput name={findOfficeForm.zipCode.name} label={t('zipCode')} maxLength={5} />
						<Button type="submit" variant="primaryAccent1" sx={{ px: 4 }} size="M">
							{t('search')}
						</Button>
					</Stack>
				</Form>
				<ButtonBase sx={{ width: 'max-content', mt: 0.5, mb: 3 }} onClick={handleUseCurrentLocation}>
					<PinDropIcon />
					<Typography variant="bodyMediumBook" sx={{ color: 'text.primary', ml: 0.5 }}>
						{t('useLocation')}
					</Typography>
				</ButtonBase>

				{foundLocations?.map((center, index) => (
					<Box
						key={index}
						sx={{
							borderRadius: 1,
							py: '12px',
							px: '16px',
							maxWidth: '525px',
							border: '1px solid',
							borderColor: 'grey.A400',
							mb: 3,
						}}
					>
						<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
							<Typography variant="bodyMediumSemiBold" sx={{ color: 'text.primary' }}>
								ClearChoice {center?.name}
							</Typography>
							{RenderDistance(center, 'text.tertiary', t('miles'))}
						</Stack>
						<Stack spacing={2}>
							<Box>
								<Typography
									component={Link}
									sx={{ color: 'text.interactive', mb: 2 }}
									variant="textLinkSmall"
									href={`https://www.google.com/maps?q=${encodeURIComponent(
										`${center?.address?.address1} ${center?.address?.city}, ${center?.address?.stateCode} ${center?.address?.zipCode}`
									)}`}
									target="_blank"
									rel="noopener noreferrer"
								>
									{center?.address?.address1} {center?.address?.city}, {center?.address?.stateCode}{' '}
									{center?.address?.zipCode}
								</Typography>
								<Typography
									sx={{ color: 'text.interactive', display: 'block', mt: 0.5 }}
									variant="textLinkSmall"
								>
									<TAGLink href={`tel:${center?.phoneNumber}`} linkSx={{ color: 'text.interactive' }}>
										{center?.phoneNumber}
									</TAGLink>
								</Typography>
							</Box>
							<Button
								variant="primaryHC"
								sx={{ border: '1px solid', width: 'max-content' }}
								onClick={() => handleChooseLocation(center)}
								size="S"
							>
								{t('selectCenter')}
							</Button>
						</Stack>
					</Box>
				))}
			</Stack>
		</Drawer>
	);
};

export default FindOfficeDrawer;
